import React, { useContext, useEffect } from 'react';
import { graphql, PageRendererProps } from 'gatsby';

import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';

import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

import Button from '@repo/ui-components/button/Button';
import Hero from '@repo/ui-components/hero/Hero';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import { GetSanityPageQuery } from '@repo/models/gatsby-graphql-types';

import Layout from '@components/layout';
import theme from '@components/theme';
import getInternalLink from '@components/utils/getInternalLink';

type SanityPageProps = {
  data: GetSanityPageQuery;
} & PageRendererProps;

function SanityPage({ data, location }: SanityPageProps) {
  const {
    SEOMetadata,
    title,
    description,
    image,
    blockBuilder,
    includePageBackground,
    minimalHeader,
    minimalFooter,
    hero,
  } = data.sanityPage;

  const { setDealerGateModalActive, storedDealer } =
    useContext(DealerSearchContext);

  useEffect(() => {
    // if blockBuilder.blocks has a block with _type blockBuilderProductsComparisonGrid
    // activate the dealer gate modal

    if (blockBuilder.blocks && !storedDealer) {
      blockBuilder.blocks.forEach(block => {
        if (block._type === 'blockBuilderProductsComparisonGrid') {
          setDealerGateModalActive(true);
        }

        // also look in nested blocks in _type "blockBuilderReusableBlock"
        if (block._type === 'blockBuilderReusableBlock') {
          // Check if blocks is an array before iterating
          if (Array.isArray(block.reusableBlockRef?.blocks)) {
            block.reusableBlockRef.blocks.forEach(reusableBlock => {
              if (
                reusableBlock._type === 'blockBuilderProductsComparisonGrid'
              ) {
                setDealerGateModalActive(true);
              }
            });
          }
        }
      });
    }
  }, [blockBuilder.blocks, setDealerGateModalActive, storedDealer]);

  return (
    <Layout
      location={location}
      headTagData={{
        title: SEOMetadata?.title || title,
        description: SEOMetadata?.description || description,
        canonicalUrl: SEOMetadata?.canonicalUrl,
        noIndex: SEOMetadata?.noIndex,
        noFollow: SEOMetadata?.noFollow,
        meta: createOpenGraphTags(
          {
            title: SEOMetadata?.ogTitle || SEOMetadata?.title || title,
            description:
              SEOMetadata?.ogDescription ||
              SEOMetadata?.description ||
              description,
            url:
              SEOMetadata?.ogUrl ||
              SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              '/',
            image: SEOMetadata?.ogImage || image,
          },
          'invisiblefence'
        ),
      }}
      fullWidth
      minimalHeader={minimalHeader}
      minimalFooter={minimalFooter}
    >
      <PageWrapper
        background={includePageBackground ? theme.colors.background : 'inherit'}
        padding={
          title === 'Home Page' ? '0 1.625rem 1.625rem 1.625rem' : '1.625rem'
        }
      >
        {hero && <Hero hero={hero} getInternalLink={getInternalLink} />}

        {blockBuilder?.blocks && (
          <BlockBuilder
            blocks={blockBuilder?.blocks}
            getInternalLink={getInternalLink}
            Button={Button}
            headerColor={theme?.header?.headerColor}
          />
        )}
      </PageWrapper>
    </Layout>
  );
}

export const query = graphql`
  query GetSanityPage($id: String!) {
    sanityPage(id: { eq: $id }, siteIdentifier: { eq: "ifb" }) {
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      title
      image {
        asset {
          _id
          url
        }
        crop {
          bottom
          left
          right
          top
        }
        hotspot {
          x
          y
        }
        alt
      }
      description
      blockBuilder {
        blocks: _rawBlocks(resolveReferences: { maxDepth: 16 })
      }
      hero {
        presentation
        heroType
        compactImage
        mediaItem {
          asset {
            _id
          }
          crop {
            bottom
            left
            right
            top
          }
          hotspot {
            x
            y
          }
          alt
        }
        externalVideo
        internalVideo {
          video: asset {
            url
          }
        }
        heroPoster {
          asset {
            url
          }
        }
        headline1
        headline2
        headlineStyle
        subheadType
        portableTextHeadline: _rawPortableTextHeadline(
          resolveReferences: { maxDepth: 2 }
        )
        subheadlineStyle
        textAlignment
        enableTextShadow
        enableCta
        cta {
          ...sanityLinkFields
        }
        themeStyle
      }
      includePageBackground
      minimalHeader
      minimalFooter
    }
  }
`;

export default SanityPage;
